import { ConfirmDialog } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PillarSettingRow from './PillarSettingRow';
import UnalignedObjectivesCount from './UnalignedObjectivesCount';

import {
    useCompanyPreferences,
    useUpdateMultiplePillarsPerObjective,
    useUpdateObjectiveWithoutPillar,
} from '@/api/performance-company-preferences';
import { useGetPillarObjectiveAlignment } from '@/api/pillars';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';

/**
 * The company settings that govern how objectives line up with the pillars, inside the collapsible
 * bar that names them: whether an objective may carry several pillars or none. Turning the
 * several-pillars setting off while objectives still carry several asks first — they keep them, and
 * can then only lose some. When that count could not be loaded, it asks anyway, without a number.
 */
export default function PillarAlignmentSettings() {
    const { t } = useTranslation();
    const { handleError } = useError();
    const { data: preferences, isError: hasPreferencesError } = useCompanyPreferences();
    const { data: alignment, isError: hasAlignmentError } = useGetPillarObjectiveAlignment();
    const updateMultiplePillars = useUpdateMultiplePillarsPerObjective();
    const updateObjectiveWithoutPillar = useUpdateObjectiveWithoutPillar();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const severalPillarsObjectivesCount = alignment?.severalPillarsObjectivesCount ?? 0;
    const isAlignmentPending = !alignment && !hasAlignmentError;

    const saveMultiplePillars = async (allowMultiplePillarsPerObjective: boolean) => {
        try {
            await updateMultiplePillars.mutateAsync({ allowMultiplePillarsPerObjective });
            showSuccessToast({ message: t('pillars.multiplePillars.saveSuccess') });
        } catch (error) {
            handleError({ error });
        } finally {
            setIsConfirmOpen(false);
        }
    };

    const saveObjectiveWithoutPillar = async (allowObjectiveWithoutPillar: boolean) => {
        try {
            await updateObjectiveWithoutPillar.mutateAsync({ allowObjectiveWithoutPillar });
            showSuccessToast({ message: t('pillars.objectiveWithoutPillar.saveSuccess') });
        } catch (error) {
            handleError({ error });
        }
    };

    const handleMultiplePillarsToggle = (allowMultiplePillarsPerObjective: boolean) => {
        if (
            !allowMultiplePillarsPerObjective &&
            (hasAlignmentError || severalPillarsObjectivesCount > 0)
        ) {
            setIsConfirmOpen(true);
            return;
        }

        void saveMultiplePillars(allowMultiplePillarsPerObjective);
    };

    const renderRows = () => {
        if (hasPreferencesError) {
            return (
                <p
                    role={'alert'}
                    className={'text-error py-4 text-[13px] leading-[19px]'}
                >
                    {t('pillars.multiplePillars.loadError')}
                </p>
            );
        }

        if (!preferences) {
            return null;
        }

        return (
            <div className={'divide-base-100 divide-y'}>
                <PillarSettingRow
                    label={t('pillars.multiplePillars.label')}
                    isChecked={preferences.allowMultiplePillarsPerObjective}
                    // Turning it off waits for the count of objectives that carry several pillars.
                    isDisabled={
                        updateMultiplePillars.isPending ||
                        (preferences.allowMultiplePillarsPerObjective && isAlignmentPending)
                    }
                    onChange={handleMultiplePillarsToggle}
                    dataTestId={'multiple-pillars-toggle'}
                />

                <PillarSettingRow
                    label={t('pillars.objectiveWithoutPillar.label')}
                    help={t('pillars.objectiveWithoutPillar.help')}
                    isChecked={preferences.allowObjectiveWithoutPillar}
                    isDisabled={updateObjectiveWithoutPillar.isPending}
                    onChange={(allowObjectiveWithoutPillar) =>
                        void saveObjectiveWithoutPillar(allowObjectiveWithoutPillar)
                    }
                    state={
                        <UnalignedObjectivesCount
                            count={alignment?.unalignedObjectivesCount}
                            hasError={hasAlignmentError}
                        />
                    }
                    dataTestId={'objective-without-pillar-toggle'}
                />
            </div>
        );
    };

    return (
        <div data-testid={'pillar-alignment-settings'}>
            {renderRows()}

            <ConfirmDialog
                isOpen={isConfirmOpen}
                title={t('pillars.multiplePillars.confirmTitle')}
                message={
                    hasAlignmentError
                        ? t('pillars.multiplePillars.confirmMessageWithoutCount')
                        : t('pillars.multiplePillars.confirmMessage', {
                              count: severalPillarsObjectivesCount,
                          })
                }
                confirmLabel={t('pillars.multiplePillars.confirmLabel')}
                cancelLabel={t('pillars.cancel')}
                onConfirm={() => void saveMultiplePillars(false)}
                onCancel={() => setIsConfirmOpen(false)}
                isLoading={updateMultiplePillars.isPending}
                zIndex={50}
                dataTestId={'multiple-pillars-confirm'}
            />
        </div>
    );
}
